import React, { useEffect, useState } from "react";
import "./CommonStyleSheet/HelpPage.css";

type HelpSlide = {
  title: string;
  image: string;
  caption: string;
  intro: string;
  steps: string[];
  tip: string;
};

const helpSlides: HelpSlide[] = [
  {
    title: "Open the Components page",
    image: "1_Components_page.png",
    caption: "Components page showing all available products.",
    intro:
      "Select Components from the top navigation bar to view the parts catalogs available to you.",
    steps: [
      "Click Components in the top navigation bar.",
      "Use Refresh to reload the latest component list.",
      "Choose View on the required component card.",
    ],
    tip: "Use the component name and image to confirm that you are opening the correct equipment.",
  },
  {
    title: "Review the figures for a component",
    image: "2_View_page.png",
    caption: "Component page showing the available figure list.",
    intro:
      "The component page lists every available figure, along with the title, number of parts, revision and date.",
    steps: [
      "Review the component and revision information on the left.",
      "Locate the required figure in the Parts Catalog table.",
      "Click View to open the detailed illustration and bill of materials.",
    ],
    tip: "The PDF button downloads the catalog in the selected language. It is optional when you are working directly in niAuthor.",
  },
  {
    title: "Choose your preferred language",
    image: "3_Language_selection.png",
    caption: "Language selector used to localize figure titles and part descriptions.",
    intro:
      "Use the Language list to display the catalog in your preferred language before opening a figure.",
    steps: [
      "Open the Language dropdown.",
      "Select the required language, such as English or Spanish.",
      "Confirm that the figure title changes to the selected language.",
    ],
    tip: "The selected language is also used for the detailed parts list and PDF output.",
  },
  {
    title: "Use the illustration and parts list together",
    image: "4_Detail_view.png",
    caption:
      "Detailed figure view with the illustration on the left and bill of materials on the right.",
    intro:
      "The detailed view helps you visually identify a part and verify its item number, part number, description, price and quantity.",
    steps: [
      "Select a callout in the illustration or a checkbox in the parts list.",
      "The corresponding callout and row are highlighted together.",
      "Use zoom, pan and reset controls to inspect the illustration closely.",
      "Select Back to Figure List to return to all figures for the component.",
    ],
    tip: "Cross-check the callout number with the Pos. or Item number before adding a part.",
  },
  {
    title: "Add a part from the bill of materials",
    image: "5_add_cart_button.png",
    caption: "Add action available for each part in the bill of materials.",
    intro:
      "When the required part has been identified, use the Add action in the same row to begin adding it to the cart.",
    steps: [
      "Find the required part row.",
      "Verify the part number, description and price.",
      "Click Add in the Action column.",
    ],
    tip: "A selected checkbox only highlights the part; the Add action is what opens the cart dialog.",
  },
  {
    title: "Set the required quantity",
    image: "6_add_cart_popup.png",
    caption: "Quantity dialog displayed before adding the part to the cart.",
    intro:
      "The add-to-cart dialog confirms the selected part and allows you to set the required quantity.",
    steps: [
      "Check whether the part is already in the cart.",
      "Use the minus and plus buttons to adjust the quantity.",
      "Review the calculated line total.",
      "Click Add to cart.",
    ],
    tip: "Choose Cancel to close the dialog without changing the cart.",
  },
  {
    title: "Open the current cart",
    image: "7_click_number.png",
    caption: "Cart quantity indicator in the top navigation bar.",
    intro:
      "The number shown beside Cart is the current total quantity of items added.",
    steps: [
      "Check the number beside Cart in the top navigation bar.",
      "Click Cart or the quantity badge to open the active cart.",
    ],
    tip: "The badge is a quick visual check that your selected items were added successfully.",
  },
  {
    title: "Review and manage the cart",
    image: "8_cart_page.png",
    caption: "Cart page with quantity controls, totals and order actions.",
    intro:
      "Before placing the cart, verify each selected part, quantity, unit price and line total.",
    steps: [
      "Use minus or plus to correct quantities.",
      "Use the delete icon to remove an unwanted line.",
      "Select Continue Browsing to add more parts.",
      "Select Cancel Cart to discard the cart and start again.",
      "Select Place Cart when the cart is ready.",
    ],
    tip: "The subtotal updates automatically when quantities or lines change.",
  },
  {
    title: "Enter purchaser and address details",
    image: "9_Orderdetails-1.png",
    caption: "Purchaser Details page: contact and address information.",
    intro:
      "Provide the purchaser details required by Fiorentini to process the order request.",
    steps: [
      "Enter the purchaser name and surname.",
      "Provide the company, address, city, ZIP code, state or province and country.",
      "Complete every field marked with a red asterisk.",
    ],
    tip: "The information entered here is sent to Fiorentini for order processing and is not used as a saved profile in the catalog tool.",
  },
  {
    title: "Add communication and billing information",
    image: "10_Orderdetails-2.png",
    caption: "Communication, billing and additional-information fields.",
    intro:
      "Complete the communication details and add any billing or order notes that Fiorentini may need.",
    steps: [
      "Enter the required telephone number and email address.",
      "Add fax, website and VAT information when applicable.",
      "Use Additional Information for special instructions or references.",
      "Click Next to continue.",
    ],
    tip: "Review the email address carefully because it may be used for order-related communication.",
  },
  {
    title: "Confirm and place the order",
    image: "11_Orderdetails-3.png",
    caption: "Final confirmation page before the order is submitted.",
    intro:
      "The confirmation page displays the purchaser details and every item included in the order.",
    steps: [
      "Review the purchaser details.",
      "Verify part numbers, descriptions, quantities and line totals.",
      "Check the subtotal.",
      "Click Confirm & Place Order to submit the order request.",
    ],
    tip: "Use Back to correct purchaser information before submitting.",
  },
  {
    title: "View placed carts and order history",
    image: "12_placed_cart_list.png",
    caption: "List of placed carts with status, quantity and total amount.",
    intro:
      "After an order is placed, the Carts page shows your order history and the current status of each cart.",
    steps: [
      "Review the cart number and creation date.",
      "Check the status, total quantity and total amount.",
      "Click Details to open a completed cart.",
    ],
    tip: "Use the refresh icon to reload the latest cart information.",
  },
  {
    title: "Open the details of a placed cart",
    image: "13_cart_details.png",
    caption: "Placed-cart details showing all ordered lines and totals.",
    intro:
      "Cart Details provides a read-only summary of the parts included in a submitted order.",
    steps: [
      "Confirm the cart number, status and creation date.",
      "Review each ordered part and quantity.",
      "Check the number of lines, total items and subtotal.",
      "Use Back to List to return to order history.",
    ],
    tip: "Keep the cart number available when communicating with Fiorentini about an order.",
  },
  {
    title: "Search directly for a part",
    image: "14_Search.png",
    caption: "Search page used to locate a part by number or description.",
    intro:
      "Use Search when you already know the part number or part description and want to reach it quickly.",
    steps: [
      "Click Search in the top navigation bar.",
      "Enter a full or partial part number or description.",
      "Use the column filters to narrow the results when needed.",
      "Click Open to load the related figure and highlight the matching callout.",
    ],
    tip: "Search results also show the master component, figure and item number, helping you confirm the correct context before opening the part.",
  },
];

const HelpPage: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSlide = helpSlides[currentIndex];
  const imageBasePath = `${process.env.PUBLIC_URL || ""}/help`;

  const showSlide = (index: number) => {
    const nextIndex = (index + helpSlides.length) % helpSlides.length;
    setCurrentIndex(nextIndex);
    document
      .querySelector(".help-page__guide-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setCurrentIndex((value) => (value - 1 + helpSlides.length) % helpSlides.length);
      }
      if (event.key === "ArrowRight") {
        setCurrentIndex((value) => (value + 1) % helpSlides.length);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <main className="help-page">
      <section className="help-page__shell" aria-label="Help guide">
        <nav className="help-page__topics" aria-label="Help topics">
          <h2>Help Topics</h2>
          {helpSlides.map((slide, index) => (
            <button
              key={slide.title}
              type="button"
              className={`help-page__topic ${
                index === currentIndex ? "help-page__topic--active" : ""
              }`}
              onClick={() => showSlide(index)}
              aria-current={index === currentIndex ? "step" : undefined}
            >
              <span>{index + 1}</span>
              <b>{slide.title}</b>
            </button>
          ))}
        </nav>

        <article className="help-page__guide-card">
          <div className="help-page__slide-head">
            <span>Step {currentIndex + 1}</span>
            <h2>{currentSlide.title}</h2>
            <p>{currentSlide.intro}</p>
          </div>

          <div className="help-page__details">
            <section className="help-page__note">
              <h3>What to do</h3>
              <ol>
                {currentSlide.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section className="help-page__note help-page__note--tip">
              <h3>Helpful tip</h3>
              <p>{currentSlide.tip}</p>
            </section>
          </div>

          <figure className="help-page__figure">
            <img
              src={`${imageBasePath}/${currentSlide.image}`}
              alt={currentSlide.caption}
              loading={currentIndex === 0 ? "eager" : "lazy"}
            />
            <figcaption>
              Figure {currentIndex + 1}. {currentSlide.caption}
            </figcaption>
          </figure>

          <div className="help-page__dots" aria-label="Step navigation">
            {helpSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                aria-label={`Go to step ${index + 1}`}
                className={`help-page__dot ${
                  index === currentIndex ? "help-page__dot--active" : ""
                }`}
                onClick={() => showSlide(index)}
              />
            ))}
          </div>

          <footer className="help-page__controls">
            <button type="button" onClick={() => showSlide(currentIndex - 1)}>
              ← Previous
            </button>
            <span>
              {currentIndex + 1} of {helpSlides.length}
            </span>
            <button type="button" onClick={() => showSlide(currentIndex + 1)}>
              Next →
            </button>
          </footer>
        </article>
      </section>
    </main>
  );
};

export default HelpPage;
